import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from 'src/app/service/auth.service';
import {
  ReportDialogData,
  ReportType,
} from 'src/app/micro-components/dialog-report/dialog-report.component';
import { ReportApiService } from 'src/app/shared/services/customer-api/report-api.service';

export type ReportScope = 'company' | 'portfolio' | 'query';

export interface ReportTypeState {
  available: boolean;
  status: string | null;
  loading: boolean;
}

export const INITIAL_REPORT_TYPE_STATE: ReportTypeState = {
  available: false,
  status: null,
  loading: false,
};

export interface ReportRegistryCtx {
  entity?: { type: string; alpha_id: string; name: string };
  folio?: { name: string; content: string[] };
}

export interface ReportDeps {
  reportApi: ReportApiService;
  auth: AuthService;
  date: string;
}

export interface ReportDefinition {
  id: ReportType;
  scope: ReportScope;
  menuTitle: string;
  tooltip(state: ReportTypeState): string;
  menuSubtitle?(state: ReportTypeState): string;
  checkStatus(ctx: ReportRegistryCtx, deps: ReportDeps): Observable<string>;
  buildDialogData(
    ctx: ReportRegistryCtx,
    state: ReportTypeState,
    deps: ReportDeps,
  ): ReportDialogData;
}

const filterCompanies = (content: string[]): string[] =>
  content.filter((e) => e.split(':')[0] === 'company');

export const REPORT_REGISTRY: ReadonlyArray<ReportDefinition> = [
  {
    id: ReportType.DueDiligenceTabular,
    scope: 'company',
    menuTitle: 'Credit Due Diligence',
    tooltip: (state) =>
      state.status === 'not_enough_data'
        ? 'Credit Due Diligence Report not available. Not enough data.'
        : 'Get an overview of key risk events ranked by severity',
    checkStatus: (ctx, { reportApi, date }) =>
      reportApi
        .getReportStatus(
          ctx.entity.alpha_id,
          ReportType.DueDiligenceTabular,
          date,
        )
        .pipe(map((res) => res['status'])),
    buildDialogData: (ctx, state) => ({
      reportType: ReportType.DueDiligenceTabular,
      entityId: ctx.entity.alpha_id,
      titleLine1: ctx.entity.name,
      titleLine2: 'Credit Due Diligence',
      status: state.status,
    }),
  },

  {
    id: ReportType.DueDiligenceTabularPortfolio,
    scope: 'portfolio',
    menuTitle: 'Credit Due Diligence (Portfolio)',
    tooltip: (state) =>
      state.status === 'not_enough_data'
        ? 'Credit Due Diligence (Portfolio) not available. Not enough data.'
        : 'Get an overview of key risk events across your portfolio, ranked by severity',
    checkStatus: (ctx, { reportApi, date }) => {
      if (!(ctx.folio?.content?.length > 0)) return of('not_available');
      const companies = filterCompanies(ctx.folio.content);
      if (companies.length === 0) return of('not_available');
      return reportApi
        .getReportStatusPortfolio(
          ctx.folio.name,
          companies,
          ReportType.DueDiligenceTabularPortfolio,
          date,
        )
        .pipe(map((res) => res['status']));
    },
    buildDialogData: (ctx, state) => {
      const companies = filterCompanies(ctx.folio.content);
      return {
        reportType: ReportType.DueDiligenceTabularPortfolio,
        entityId: ctx.entity.alpha_id,
        portfolioName: ctx.entity.name,
        portfolioContent: companies,
        titleLine1: ctx.entity.name,
        titleLine2: 'Credit Due Diligence (Portfolio)',
        messageLine:
          companies.length !== ctx.folio.content.length
            ? "Your portfolio includes entities other than companies that won't be covered by the report."
            : null,
        status: state.status,
      };
    },
  },

  {
    id: ReportType.DueDiligenceTabular,
    scope: 'query',
    menuTitle: 'Credit Due Diligence (Detailed)',
    tooltip: () => '',
    menuSubtitle: () => 'Ranked events by severity with credit assessment',
    checkStatus: (_ctx, { auth }) =>
      of(auth.query ? 'available' : 'not_available'),
    buildDialogData: (_ctx, _state, { auth }) => {
      const query = auth.query;
      let searchQuery = query.query;
      const queryName = query.name || 'Search Query';
      if (typeof searchQuery === 'string') {
        searchQuery = {
          operator: 'or',
          filters: [
            {
              negated: false,
              field: 'text',
              name: searchQuery,
              value: searchQuery,
              input: searchQuery,
            },
          ],
          groups: [],
        };
      }
      return {
        reportType: ReportType.DueDiligenceTabular,
        searchQuery,
        queryName,
        titleLine1: queryName,
        titleLine2: 'Credit Due Diligence (Detailed)',
        status: 'available',
      };
    },
  },
];

export function scopeOfContext(
  mode: 'entity' | 'query',
  entityType: string | undefined,
): ReportScope | null {
  if (mode === 'query') return 'query';
  if (entityType === 'company') return 'company';
  if (entityType === 'portfolio') return 'portfolio';
  return null;
}

export function isStatusAvailable(status: string | null): boolean {
  return (
    status !== null &&
    status !== 'not_enough_data' &&
    status !== 'not_available'
  );
}
